import { BehaviorSubject, Subject } from 'rxjs';
import { Injectable, OnDestroy } from '@angular/core';

import { URLUtils } from '../urlUtils';
import { environment } from 'src/environments/environment';

declare let Strophe: any;
declare let $pres: any;

export type XmppStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

/**
 * XmppService — singleton (providedIn: 'root') that owns the single global
 * Strophe connection for the entire application lifetime.
 *
 * Rules:
 *  - connect() is safe to call multiple times; it is a no-op when already
 *    connected or connecting.
 *  - MessagesComponent must NOT create its own Strophe.Connection. Instead it
 *    injects this service and reads connectionStatus$ / incomingMessage$.
 *  - The dashboard (or app shell) calls connect() once after login so that the
 *    connection is available before the user ever visits Messages.
 */
@Injectable({ providedIn: 'root' })
export class XmppService implements OnDestroy {
  // ── public state ──────────────────────────────────────────────────────────
  readonly connectionStatus$ = new BehaviorSubject<XmppStatus>('disconnected');
  readonly incomingMessage$ = new Subject<any>(); // raw stanza element
  readonly incomingPresence$ = new Subject<any>(); // raw presence stanza element
  readonly presenceUpdate$ = new Subject<{ guid: string; isOnline: boolean }>();
  presenceMap: Record<string, Set<string>> = {};
  subscribedJids = new Set<string>();
  rosterFetched = false; // ← ADD HERE as property
  // Expose the raw connection for callers that must use conn directly
  // (sendIQ, send, etc.).  Null until first successful connect.
  conn: any = null;

  // ── private ───────────────────────────────────────────────────────────────
  private domain = environment.xmppDomain;
  private wsUrl = `wss://${this.domain}:5443/ws`;
  private handlerAdded = false;

  get isConnected(): boolean {
    return this.connectionStatus$.value === 'connected';
  }

  // ── public API ────────────────────────────────────────────────────────────

  /**
   * Establish the XMPP connection.  Safe to call repeatedly — skipped when
   * already connected or connecting.
   */
  connect(): void {
    const status = this.connectionStatus$.value;
    if (status === 'connected' || status === 'connecting') {
      return; // already up — nothing to do
    }

    const username = `${URLUtils.get_jid()}@${this.domain}`;
    const password = localStorage.getItem('TOKEN');

    if (!username || !password) {
      console.warn(
        '[XmppService] Cannot connect — credentials not available yet.',
      );
      return;
    }

    this.connectionStatus$.next('connecting');

    this.conn = new Strophe.Connection(this.wsUrl);

    // Point the global Strophe.ui reference at this service so that
    // legacy code that uses Strophe.ui.conn / Strophe.ui.xmlParser still works
    // during the migration period.
    Strophe.ui = this as any;

    this.conn.connect(username, password, (status: any) =>
      this._onStatus(status),
    );
  }

  /**
   * Gracefully disconnect.  Called on logout or app teardown.
   */
  disconnect(): void {
    this.presenceMap = {}; // ← ADD
    this.subscribedJids.clear(); // ← ADD
    this.rosterFetched = false;
    if (this.conn) {
      try {
        this.conn.disconnect();
      } catch (_) {
        /* ignore */
      }
    }
    this.conn = null;
    this.handlerAdded = false;
    this.connectionStatus$.next('disconnected');
  }

  // ── internal ──────────────────────────────────────────────────────────────

  private _onStatus(status: any): void {
    if (status === Strophe.Status.CONNECTED) {
      if (!this.handlerAdded) {
        // Register a single message handler
        this.conn.addHandler(
          (msg: any) => {
            this.incomingMessage$.next(msg);
            return true;
          },
          null,
          'message',
        );

        // Register presence handler
        this.conn.addHandler(
          (presence: any) => {
            const from = presence.getAttribute('from') || '';
            const type = presence.getAttribute('type') || 'available';
            const skip = ['subscribe', 'subscribed', 'unsubscribe', 'unsubscribed'];

            if (from && !skip.includes(type)) {
              const bareJid = from.split('/')[0].toLowerCase();
              const resource = from.split('/')[1] || 'default';
              const guid = bareJid.split('@')[0];
              const myGuid = URLUtils.get_jid()?.toLowerCase() || '';

              if (guid !== myGuid) {
                if (!this.presenceMap[guid]) {
                  this.presenceMap[guid] = new Set();
                }
                if (type === 'unavailable') {
                  this.presenceMap[guid].delete(resource);
                } else {
                  this.presenceMap[guid].add(resource);
                }
              }
            }

            this.incomingPresence$.next(presence);
            return true;
          },
          null,
          'presence',
        );

        this.handlerAdded = true;  // ← THIS WAS MISSING
      }                              // ← CLOSING if (!this.handlerAdded)

      // Announce our presence to the server
      this.conn.send($pres().tree());
      this.connectionStatus$.next('connected');

    } else if (
      status === Strophe.Status.DISCONNECTED ||
      status === Strophe.Status.ERROR
    ) {
      this.handlerAdded = false;
      this.connectionStatus$.next(
        status === Strophe.Status.ERROR ? 'error' : 'disconnected',
      );
    } else if (status === Strophe.Status.CONNECTING) {
      this.connectionStatus$.next('connecting');
    }
  }
  // Satisfy OnDestroy (not strictly needed for root service but good practice)
  ngOnDestroy(): void {
    this.disconnect();
  }
}
